<script lang="ts">
  import { onMount } from 'svelte';
  import Alerte from '../ui/Alerte.svelte';
  import Bouton from '../ui/Bouton.svelte';
  import ChampTexte from '../ui/ChampTexte.svelte';
  import ControleFormulaire from '../ui/ControleFormulaire.svelte';
  import Formulaire from '../ui/Formulaire.svelte';
  import SelectionOrganisation from '../ui/formulaire/SelectionOrganisation.svelte';
  import type { Organisation } from '../ui/formulaire/SelectionOrganisation.types';
  import Lien from '../ui/Lien.svelte';
  import type { DonneesFormulaireDemandeAide } from './DonneesFormulaireDemandeAide';

  interface Props {
    id?: string;
    enCoursEnvoi: boolean;
    formulaireSoumis: boolean;
    erreurs: string;
    surFormulaireSoumis: (donnees: DonneesFormulaireDemandeAide) => void;
  }

  let { id = '', enCoursEnvoi, formulaireSoumis, erreurs, surFormulaireSoumis }: Props = $props();

  let formulaire: Formulaire | undefined = $state();
  let entite: Organisation | undefined = $state();
  let email = $state('');
  let libelleChampUtilisateurMAC = $state("Email de l'Aidant cyber ou du prestataire");
  let estEnRelationAvecUnUtilisateur: boolean | undefined = $state();
  let emailUtilisateurMAC = $state('');
  let identifiantAidant: string | null = $state(null);
  let cguSontValidees = $state(false);
  let utilisateurMACPrerempli = $state(false);

  onMount(() => {
    let urlSearchParams = new URLSearchParams(window.location.search);
    let parametreEmailUtilisateurMAC = urlSearchParams.get('email-utilisateur-mac');
    if (parametreEmailUtilisateurMAC) {
      utilisateurMACPrerempli = true;
      estEnRelationAvecUnUtilisateur = true;
      emailUtilisateurMAC = atob(decodeURIComponent(parametreEmailUtilisateurMAC));
      return;
    }
    let nomUsage = urlSearchParams.get('nom-usage');
    identifiantAidant = urlSearchParams.get('identifiant-utilisateur-mac');
    if (nomUsage && identifiantAidant) {
      libelleChampUtilisateurMAC = "Nom de l'Aidant cyber";
      utilisateurMACPrerempli = true;
      estEnRelationAvecUnUtilisateur = true;
      emailUtilisateurMAC = new TextDecoder().decode(
        Uint8Array.from(atob(decodeURIComponent(nomUsage)), (car) => car.charCodeAt(0))
      );
      return;
    }
  });

  export const estValide = () => formulaire?.estValide() ?? false;

  const soumetsFormulaire = () => {
    if (!entite) return;

    surFormulaireSoumis({
      entite,
      email,
      ...(estEnRelationAvecUnUtilisateur && !identifiantAidant && { emailUtilisateurMAC }),
      ...(identifiantAidant && { identifiantAidant }),
      cguSontValidees,
    });
  };
</script>

<Formulaire classe="carte-formulaire" bind:this={formulaire} {id}>
  <div class="champ recherche-organisation">
    <label class="libelle" for="entite">Recherchez votre organisation</label>
    <SelectionOrganisation bind:valeur={entite} filtreDepartement={undefined} />
    {#if entite}
      <div>Votre entreprise : {entite.nom} ({entite.departement})</div>
    {/if}
  </div>

  {#if entite || utilisateurMACPrerempli}
    <div class="champs-requis-libelle">
      <span class="requis">Champ obligatoire</span>
    </div>
    <div class="champ">
      <ChampTexte
        aideSaisie="Ex : jean.dupont@mail.com"
        id="email"
        libelle="Email de contact"
        messageErreur="Le format du mail est invalide"
        nom="email"
        requis={true}
        type="email"
        bind:valeur={email}
      />
    </div>

    <div class="champ champ-radios">
      <ControleFormulaire
        requis
        libelle="Êtes-vous déjà en contact avec un Aidant cyber ou un prestataire ayant proposé de vous accompagner dans la réalisation de ce diagnostic ?"
      >
        <div>
          <label>
            <input
              disabled={utilisateurMACPrerempli}
              name="estEnRelationAvecUnUtilisateur"
              type="radio"
              bind:group={estEnRelationAvecUnUtilisateur}
              value={false}
            />
            <span>Non</span>
          </label>
          <label>
            <input
              disabled={utilisateurMACPrerempli}
              name="estEnRelationAvecUnUtilisateur"
              type="radio"
              bind:group={estEnRelationAvecUnUtilisateur}
              value={true}
            />
            <span>Oui</span>
          </label>
        </div>
        {#if estEnRelationAvecUnUtilisateur === undefined && formulaireSoumis}
          <span class="erreur-champ-saisie">Ce champ est obligatoire. Veuillez le cocher.</span>
        {/if}
      </ControleFormulaire>
    </div>

    {#if estEnRelationAvecUnUtilisateur}
      <div class="champ champ-aidant">
        {#if identifiantAidant}
          <ChampTexte
            aideSaisie="Ex: Roger D."
            desactive={true}
            id="identifiantAidant"
            libelle={libelleChampUtilisateurMAC}
            nom="identifiantAidant"
            requis={true}
            type="text"
            bind:valeur={emailUtilisateurMAC}
          />
        {:else}
          <ChampTexte
            aideSaisie="Ex: roger.dupont@email.fr"
            desactive={utilisateurMACPrerempli}
            id="emailAidant"
            libelle={libelleChampUtilisateurMAC}
            messageErreur="Le format du mail est invalide"
            nom="emailAidant"
            requis={true}
            type="email"
            bind:valeur={emailUtilisateurMAC}
          />
        {/if}
      </div>
    {/if}

    <dsfr-checkbox
      id="cgu"
      name="cgu"
      value={cguSontValidees}
      onvaluechanged={(e: CustomEvent) => (cguSontValidees = e.detail)}
      errorMessage="Ce champ est obligatoire. Veuillez le cocher."
      required
    >
      <span>
        J'accepte les <Lien
          href="https://monaide.cyber.gouv.fr/cgu"
          blank
          libelle="conditions générales d'utilisation"
          neutre
        ></Lien> de MonAideCyber au nom de l’entité que je représente.
      </span>
    </dsfr-checkbox>
    <div>
      <Bouton
        type="primaire"
        taille="md"
        libelle="Envoyer ma demande"
        surClic={soumetsFormulaire}
        desactive={enCoursEnvoi}
      />
    </div>

    {#if erreurs}
      <div class="alerte-erreur">
        <Alerte type="erreur" taille="md" titre="Une erreur est survenue">
          {erreurs}
        </Alerte>
      </div>
    {/if}
  {/if}
</Formulaire>

<style lang="scss">
  @use '../../../assets/styles/responsive' as *;

  :global(.carte-formulaire) {
    display: flex;
    flex-direction: column;
    max-width: 792px;
    margin: auto;
    border-radius: 8px;
    border: 1px solid var(--border-default-grey);
    background: #fff;
    padding: 48px 16px 72px 16px;

    @include a-partir-de(md) {
      padding: 48px 80px 72px 80px;
    }

    @include a-partir-de(lg) {
      padding: 48px 80px 72px 80px;
    }
  }

  .libelle {
    font-size: 1.25rem;
    font-weight: bold;
  }

  .champ {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .champ-aidant {
    position: relative;
    top: -8px;
  }

  .recherche-organisation {
    gap: 16px;
  }

  .champ.champ-radios {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .erreur-champ-saisie {
      display: flex;
    }
  }

  .requis:before {
    content: '*';
    color: var(--text-default-error);
    margin-right: 4px;
    font-size: 1rem;
  }

  .champs-requis-libelle {
    display: flex;
    justify-content: end;
    font-size: 0.875rem;
    margin-top: 16px;
    margin-bottom: 16px;
  }

  dsfr-checkbox {
    margin-bottom: 32px;
  }

  .alerte-erreur {
    margin-top: 32px;
  }
</style>
